export type DelayEstimate =
  | {
      readonly kind: "delay"
      readonly samples: number
      readonly confidence: number
    }
  | {
      readonly kind: "insufficient_signal"
      readonly reason: "low_energy" | "ambiguous_peak"
    }

function nextPowerOfTwo(value: number): number {
  let size = 1
  while (size < value) size *= 2
  return size
}

function transform(real: Float64Array, imaginary: Float64Array, inverse: boolean): void {
  const size = real.length
  for (let index = 1, reversed = 0; index < size; index += 1) {
    let bit = size >> 1
    while ((reversed & bit) !== 0) {
      reversed ^= bit
      bit >>= 1
    }
    reversed ^= bit
    if (index < reversed) {
      const realValue = real[index] ?? 0
      const imaginaryValue = imaginary[index] ?? 0
      real[index] = real[reversed] ?? 0
      imaginary[index] = imaginary[reversed] ?? 0
      real[reversed] = realValue
      imaginary[reversed] = imaginaryValue
    }
  }

  for (let width = 2; width <= size; width *= 2) {
    const angle = ((inverse ? 2 : -2) * Math.PI) / width
    const stepReal = Math.cos(angle)
    const stepImaginary = Math.sin(angle)
    for (let start = 0; start < size; start += width) {
      let twiddleReal = 1
      let twiddleImaginary = 0
      for (let offset = 0; offset < width / 2; offset += 1) {
        const even = start + offset
        const odd = even + width / 2
        const oddReal = real[odd] ?? 0
        const oddImaginary = imaginary[odd] ?? 0
        const productReal = oddReal * twiddleReal - oddImaginary * twiddleImaginary
        const productImaginary = oddReal * twiddleImaginary + oddImaginary * twiddleReal
        const evenReal = real[even] ?? 0
        const evenImaginary = imaginary[even] ?? 0
        real[even] = evenReal + productReal
        imaginary[even] = evenImaginary + productImaginary
        real[odd] = evenReal - productReal
        imaginary[odd] = evenImaginary - productImaginary
        const nextReal = twiddleReal * stepReal - twiddleImaginary * stepImaginary
        twiddleImaginary = twiddleReal * stepImaginary + twiddleImaginary * stepReal
        twiddleReal = nextReal
      }
    }
  }

  if (inverse) {
    for (let index = 0; index < size; index += 1) {
      real[index] = (real[index] ?? 0) / size
      imaginary[index] = (imaginary[index] ?? 0) / size
    }
  }
}

function rootMeanSquare(samples: Float32Array): number {
  let energy = 0
  for (const sample of samples) energy += sample * sample
  return Math.sqrt(energy / samples.length)
}

export function estimateDelay(
  reference: Float32Array,
  delayed: Float32Array,
  maximumLagSamples: number,
): DelayEstimate {
  const sampleCount = Math.min(reference.length, delayed.length)
  if (sampleCount < 32 || rootMeanSquare(reference) < 0.0001 || rootMeanSquare(delayed) < 0.0001) {
    return { kind: "insufficient_signal", reason: "low_energy" }
  }

  const size = nextPowerOfTwo(sampleCount * 2)
  const referenceReal = new Float64Array(size)
  const referenceImaginary = new Float64Array(size)
  const delayedReal = new Float64Array(size)
  const delayedImaginary = new Float64Array(size)
  for (let index = 0; index < sampleCount; index += 1) {
    const window = 0.5 - 0.5 * Math.cos((2 * Math.PI * index) / (sampleCount - 1))
    referenceReal[index] = (reference[index] ?? 0) * window
    delayedReal[index] = (delayed[index] ?? 0) * window
  }
  transform(referenceReal, referenceImaginary, false)
  transform(delayedReal, delayedImaginary, false)

  for (let index = 0; index < size; index += 1) {
    const referenceComponentReal = referenceReal[index] ?? 0
    const referenceComponentImaginary = referenceImaginary[index] ?? 0
    const delayedComponentReal = delayedReal[index] ?? 0
    const delayedComponentImaginary = delayedImaginary[index] ?? 0
    const crossReal =
      delayedComponentReal * referenceComponentReal +
      delayedComponentImaginary * referenceComponentImaginary
    const crossImaginary =
      delayedComponentImaginary * referenceComponentReal -
      delayedComponentReal * referenceComponentImaginary
    const magnitude = Math.hypot(crossReal, crossImaginary)
    referenceReal[index] = magnitude > 1e-12 ? crossReal / magnitude : 0
    referenceImaginary[index] = magnitude > 1e-12 ? crossImaginary / magnitude : 0
  }
  transform(referenceReal, referenceImaginary, true)

  let bestLag = 0
  let bestPeak = -Infinity
  let secondPeak = -Infinity
  for (let lag = -maximumLagSamples; lag <= maximumLagSamples; lag += 1) {
    const index = lag < 0 ? size + lag : lag
    const peak = Math.abs(referenceReal[index] ?? 0)
    if (peak > bestPeak) {
      secondPeak = bestPeak
      bestPeak = peak
      bestLag = lag
    } else if (peak > secondPeak) {
      secondPeak = peak
    }
  }

  const ratio = bestPeak / Math.max(secondPeak, 1e-12)
  if (ratio < 1.15) return { kind: "insufficient_signal", reason: "ambiguous_peak" }
  const peakIndex = bestLag < 0 ? size + bestLag : bestLag
  const previousIndex = (peakIndex - 1 + size) % size
  const nextIndex = (peakIndex + 1) % size
  const previous = Math.abs(referenceReal[previousIndex] ?? 0)
  const current = Math.abs(referenceReal[peakIndex] ?? 0)
  const next = Math.abs(referenceReal[nextIndex] ?? 0)
  const denominator = previous - 2 * current + next
  const fractionalOffset =
    Math.abs(denominator) > 1e-12 ? (0.5 * (previous - next)) / denominator : 0
  return {
    kind: "delay",
    samples: bestLag + fractionalOffset,
    confidence: Math.min(1, (ratio - 1) / 2),
  }
}
